/**
 * RendSkillEffect — Blades tree rend path: dragging lateral cut that applies bleed.
 *
 * Short-range single-target melee attack in a lateral arc. On hit, applies
 * bleed stacks via StatusEffectSystem. Bleed uses intensity stacking: each
 * stack adds damage per tick, and duration refreshes on re-application.
 *
 * Supports upgrade keys via resolver:
 * - Lingering Wound: `bleedDurationBonusMs` (additive) — extends bleed duration
 * - Deep Cut: `bleedStacksApplied` (max) — applies 2 stacks per hit instead of 1
 *
 * Implements SkillEffect directly with own cooldown management
 * (InstantSkillEffect has no cooldown). Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see blades-spec.md § Rend
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface RendDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly combat: {
    startAttack(attackType: string): number; // returns enemies hit
  };
  readonly statusEffects: {
    apply(effectId: string, config: {
      sourceSkillId: string;
      durationMs: number;
      intensity: number;
      stacks: number;
    }): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface RendConfig {
  readonly cooldownMs: number;
  readonly baseDurationMs: number;
  readonly bleedIntensity: number;
  readonly maxBleedStacks: number;
}

const DEFAULT_CONFIG: RendConfig = {
  cooldownMs: 1000,                   // medium cooldown tier
  baseDurationMs: 6000,               // 6s base bleed duration
  bleedIntensity: 3,                  // 3 damage per tick per stack
  maxBleedStacks: 3,                  // rend-specific stack cap
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class RendSkillEffect implements SkillEffect {
  readonly skillId = 'rend';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: RendDeps;
  private readonly config: RendConfig;

  private _cooldownRemaining = 0;
  private _attacksFired = 0;
  private _bleedApplications = 0;
  private _resolvedDurationMs = 0;
  private _resolvedStacksPerHit = 1;

  constructor(deps: RendDeps, config: Partial<RendConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get attacksFired(): number { return this._attacksFired; }
  get bleedApplications(): number { return this._bleedApplications; }
  get resolvedDurationMs(): number { return this._resolvedDurationMs; }
  get resolvedStacksPerHit(): number { return this._resolvedStacksPerHit; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve bleed duration (base + Lingering Wound bonus)
    const durationBonus = this.deps.resolver.getAdditiveEffect('rend', 'bleedDurationBonusMs');
    this._resolvedDurationMs = this.config.baseDurationMs + durationBonus;

    // Resolve stacks per hit (1 base, 2 with Deep Cut)
    this._resolvedStacksPerHit = Math.max(
      1,
      this.deps.resolver.getMaxEffect('rend', 'bleedStacksApplied') ?? 1,
    );

    // Fire the lateral cut
    const enemiesHit = this.deps.combat.startAttack('rend');
    this._attacksFired++;

    // Apply bleed on hit
    if (enemiesHit > 0) {
      this.deps.statusEffects.apply('bleed', {
        sourceSkillId: 'rend',
        durationMs: this._resolvedDurationMs,
        intensity: this.config.bleedIntensity,
        stacks: this._resolvedStacksPerHit,
      });
      this._bleedApplications++;
    }

    // Start cooldown
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false; // instant skill
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
